/**
 * Client entry point.
 *
 * Two surfaces in one document, as in the prototype: the marketing page, and the
 * family app as a full-screen overlay. The app's code is a separate chunk, so a
 * visitor who only reads the marketing page never downloads the tree renderer.
 */

import { loadMe } from './api/auth.js';
import { ApiError, refreshAccessToken } from './api/client.js';
import { esc, render } from './lib/dom.js';
import { router } from './router.js';
import { session } from './state/session.js';

const siteRoot = document.getElementById('site');
const appRoot = document.getElementById('app-root');

/** Cleanup returned by the currently mounted screen. */
let unmountCurrent = null;

/**
 * Take down the loader that `index.html` paints before this file runs.
 *
 * Called after the first screen has rendered rather than when the bundle finishes parsing: the
 * loader exists to cover the gap until there is something to look at, and removing it a moment
 * early puts the blank page back right at the end.
 *
 * `pointer-events: none` goes on first. The element is a fixed full-screen overlay, so for the
 * length of the fade it is invisible and still in front of everything — and a press landing in
 * that window hits the overlay instead of the button under it. On the sign-in page that is the
 * sign-in button.
 *
 * Removed rather than left hidden, so nothing is parked on top of the app at all.
 */
function dismissBootLoader() {
  const boot = document.getElementById('boot');
  if (!boot) return;

  boot.style.pointerEvents = 'none';
  boot.style.transition = 'opacity .18s ease';
  boot.style.opacity = '0';
  setTimeout(() => boot.remove(), 200);
}

async function boot() {
  // A stored refresh token means the user was signed in when they last closed the
  // tab. Recover the session before the first route resolves, so a reload lands
  // back where they were rather than bouncing them to sign-in.
  if (session.refreshToken) {
    try {
      // Refresh first. The access token is deliberately memory-only, so there is
      // never one after a reload — and letting `loadMe()` discover that costs a 401
      // and a replay, three requests instead of two on every load. These families
      // are on mid-range Android phones; a wasted round trip on boot is the kind
      // this project is supposed to be careful about.
      if (!session.accessToken) await refreshAccessToken();
      await loadMe();
    } catch (error) {
      // An expired or revoked refresh token is normal, not exceptional.
      if (error instanceof ApiError && error.status === 401) session.clear();
      else console.warn('Could not restore session:', error);
    }
  }

  router.addEventListener('render', onRender);
  router.addEventListener('render-loading', onLoading);
  router.addEventListener('render-placeholder', onPlaceholder);
  router.addEventListener('not-found', onNotFound);

  // Being removed from a tree mid-session must not leave every screen erroring.
  session.on('tree-changed', () => router.resolve(window.location.pathname));

  await router.start();
}

/**
 * Between the press and the screen.
 *
 * A route's module is a request of its own, and until it arrived the previous screen stayed on
 * display — so pressing "Account" from Video Messages left Video Messages up, and the press
 * looked ignored. This paints the shell's own loading state as soon as navigation begins, and
 * the screen replaces it a moment later.
 *
 * Only for app routes. The marketing page is one document with no shell to put a spinner in,
 * and it is served instantly from the same bundle.
 */
async function onLoading(event) {
  const { route } = event.detail;
  if (!route.auth) return;

  // The screen about to mount will unmount this one itself, but its cleanup must not run
  // against a stage this function replaced underneath it.
  if (unmountCurrent) {
    unmountCurrent();
    unmountCurrent = null;
  }

  siteRoot.hidden = true;
  appRoot.hidden = false;

  const { ensureShell, paintRail } = await import('./app/shell.js');
  const stage = ensureShell(appRoot);
  paintRail();
  render(stage, '<div class="stage"><div class="treewrap skeleton"><div class="spinner"></div></div></div>');
}

async function onRender(event) {
  const { route, params, module } = event.detail;

  if (unmountCurrent) {
    unmountCurrent();
    unmountCurrent = null;
  }

  const surface = module.meta?.surface ?? (route.auth ? 'app' : 'site');
  const container = surface === 'site' ? siteRoot : appRoot;

  siteRoot.hidden = surface !== 'site';
  appRoot.hidden = surface === 'site';

  if (module.meta?.title) document.title = module.meta.title;

  const query = Object.fromEntries(new URLSearchParams(window.location.search));

  unmountCurrent = module.mount(container, { params, query, route }) ?? null;

  dismissBootLoader();
}

/**
 * Placeholder for a screen whose backend app is not built yet.
 *
 * Names the screen and the phase it lands in, so it is always obvious whether
 * something is missing or broken. A blank page or a 404 here would be read as a
 * bug during development, and the routes are all in the table from day one.
 *
 * **Rendered inside the shell**, which is the whole point of this rewrite. It used to
 * render straight into `appRoot`, replacing the header and the navigation rail along
 * with the screen. So one click on any greyed rail item wiped the rail — and with the
 * rail gone there was nothing left to click. It read as "the app stopped working",
 * not as "that screen is not built", which is exactly backwards from what this
 * placeholder exists to communicate.
 */
async function onPlaceholder(event) {
  const { route } = event.detail;

  if (unmountCurrent) {
    unmountCurrent();
    unmountCurrent = null;
  }

  siteRoot.hidden = true;
  appRoot.hidden = false;

  // Loaded on demand: a visitor on the marketing page never needs the shell.
  const { ensureShell, paintRail, labelFor } = await import('./app/shell.js');
  const stage = ensureShell(appRoot);
  paintRail();

  // The rail's label, not the route key. "onthis" is an internal identifier and reads
  // as a defect to anyone who did not write it.
  const name = labelFor(route.path) ?? route.key;
  document.title = `${name} · Linyay`;

  render(
    stage,
    `
    <div class="stage">
      <div class="panel" style="max-width:640px;margin:40px auto">
        <span class="pill">Not built yet</span>
        <h4 style="font-family:var(--display);font-weight:400;font-size:1.4rem;margin:.6em 0 .3em">
          ${esc(name)}
        </h4>
        <p style="color:var(--ink-2);font-size:.92rem">
          This screen lands in <b>Phase ${esc(route.phase ?? '?')}</b>. The route and the
          design exist; the backend app it reads from is not built yet.
        </p>
        <p class="small" style="margin-top:14px">
          See <code>plans/PROJECT_PLAN.md</code> §17 for what each phase ships.
        </p>
        <div style="margin-top:18px">
          <a class="btn ghost sm" href="/app">← Back to Today</a>
        </div>
      </div>
    </div>
    `,
  );

  dismissBootLoader();
}

/**
 * An unknown URL.
 *
 * Kept inside the shell for a signed-in relative, so a mistyped or stale link leaves
 * them somewhere they can navigate from rather than on an island with one button. A
 * visitor who is not signed in has no shell to keep, and gets the bare card.
 */
async function onNotFound(event) {
  if (unmountCurrent) {
    unmountCurrent();
    unmountCurrent = null;
  }

  siteRoot.hidden = true;
  appRoot.hidden = false;

  const card = `
    <div class="panel" style="max-width:560px;margin:40px auto;text-align:center">
      <h4 style="font-family:var(--display);font-weight:400;font-size:1.6rem">
        That page is not here.
      </h4>
      <p style="color:var(--ink-2);font-size:.92rem">
        The link may be old, or the person who shared it may have moved something.
      </p>
      <div style="margin-top:18px">
        <a class="btn" href="${session.isAuthenticated ? '/app' : '/'}">
          ${session.isAuthenticated ? 'Go to Today' : 'Go to the home page'}
        </a>
      </div>
    </div>`;

  if (!session.isAuthenticated) {
    render(appRoot, card);
    // This branch returns early, so it takes the loader down itself. Without it, a bad link
    // opened by somebody who is not signed in spins for ever over a perfectly good message.
    dismissBootLoader();
    return;
  }

  const { ensureShell, paintRail } = await import('./app/shell.js');
  const stage = ensureShell(appRoot);
  paintRail();
  render(stage, `<div class="stage">${card}</div>`);

  dismissBootLoader();
}

boot().catch((error) => {
  console.error('Failed to start Linyay:', error);
  // The loader comes down here too. Left up, it would sit on top of the message explaining
  // why nothing loaded — a spinner that never stops, which is the worst of the options.
  dismissBootLoader();
  document.body.insertAdjacentHTML(
    'afterbegin',
    `<div style="padding:24px;font-family:Georgia,serif;color:#96442D">
       Linyay could not start. Please reload the page.
     </div>`,
  );
});
